import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { Auth, Print, System, Tenants } from '@/lib/api';
import { Button, Card, Icon, Input, Pill, Skeleton, Badge, Spinner } from '@/design-system';
import { Logo } from '@/components/Logo';
import { useSession, usePrefs, useToasts } from '@/lib/store';
import { useTheme, type ThemeName } from '@/lib/theme';
import { appBuildSha, appVersion, formattedBuildTime } from '@/lib/version';

export function SettingsPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user, memberships, signOut } = useSession();
  const { activeTenantId, setActiveTenantId } = usePrefs();
  const { push } = useToasts();

  const tenantsQ = useQuery({ queryKey: ['tenants'], queryFn: () => Tenants.list(), enabled: Boolean(user) });
  const healthQ = useQuery({ queryKey: ['health'], queryFn: () => System.health() });
  const printersQ = useQuery({ queryKey: ['print.printers'], queryFn: () => Print.printers() });

  const handleSignOut = async () => {
    try {
      await Auth.logout();
    } catch {
      /* ignore */
    } finally {
      signOut();
      void qc.clear();
      navigate('/login', { replace: true });
    }
  };

  const tenants = tenantsQ.data?.tenants ?? [];
  const active = tenants.find((tn) => tn.id === activeTenantId) ?? tenants[0];
  const myMembership = memberships.find((m) => m.tenantId === active?.id);

  return (
    <div className="grid gap-6 max-w-4xl pb-12">
      <header>
        <h1 className="ui-section-title text-3xl">{t('settings.title')}</h1>
        <p className="text-sm text-mist">Konto, preferanser og system.</p>
      </header>

      {/* Profile */}
      <ProfileCard />

      {/* Active household */}
      <Card className="grid gap-4 p-6">
        <div className="flex items-center gap-3">
          <Icon name="container" className="text-candle" />
          <h2 className="font-serif text-xl text-parchment">Aktiv husholdning</h2>
        </div>
        {tenantsQ.isLoading ? (
          <Skeleton height={56} />
        ) : tenants.length === 0 ? (
          <div className="text-sm text-mist">
            Du er ikke medlem av noen husholdning enda.{' '}
            <Link to="/onboarding/create-household" className="text-candle">Opprett en →</Link>
          </div>
        ) : (
          <div className="grid gap-2">
            {tenants.map((tn) => {
              const ms = memberships.find((m) => m.tenantId === tn.id);
              const isActive = tn.id === active?.id;
              return (
                <button
                  key={tn.id}
                  type="button"
                  onClick={() => {
                    setActiveTenantId(tn.id);
                    void qc.invalidateQueries();
                    push({ title: `Byttet til ${tn.name}`, tone: 'success' });
                  }}
                  className={`ui-card flex items-center justify-between gap-2 p-3 text-left transition-colors ${
                    isActive ? 'border-candle' : 'hover:border-line-soft'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="grid h-9 w-9 place-items-center rounded-full bg-candle/10 text-candle">
                      <Icon name="container" size={14} />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-parchment">{tn.name}</div>
                      <div className="text-[10px] uppercase tracking-widest text-mist">{tn.plan} · {tn.slug}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {ms && <Badge tone="mist">{ms.role}</Badge>}
                    {isActive && <Icon name="check-circle" size={14} className="text-candle" />}
                  </div>
                </button>
              );
            })}
            <div className="flex justify-between pt-2">
              <Link to="/onboarding/create-household">
                <Button variant="ghost" iconLeft="plus" size="sm">Ny husholdning</Button>
              </Link>
              {myMembership?.role === 'OWNER' && (
                <Link to="/admin"><Button variant="ghost" iconLeft="users" size="sm">Administrer medlemmer</Button></Link>
              )}
            </div>
          </div>
        )}
      </Card>

      {/* Preferences */}
      <Card className="grid gap-5 p-6">
        <div className="flex items-center gap-3">
          <Icon name="sliders" className="text-candle" />
          <h2 className="font-serif text-xl text-parchment">Preferanser</h2>
        </div>

        {/* Language */}
        <div className="grid gap-2">
          <div className="text-xs uppercase tracking-widest text-mist">{t('settings.language')}</div>
          <div className="flex flex-wrap gap-2">
            <Pill active={i18n.language.startsWith('nb')} onClick={() => i18n.changeLanguage('nb')}>
              🇳🇴 {t('settings.languageNb')}
            </Pill>
            <Pill active={i18n.language.startsWith('en')} onClick={() => i18n.changeLanguage('en')}>
              🇬🇧 {t('settings.languageEn')}
            </Pill>
          </div>
        </div>

        {/* Theme */}
        <ThemeSection />

        {/* Print target */}
        <PrintTargetSection printersQ={printersQ} />

        {/* Notifications */}
        <NotificationsSection />
      </Card>

      {/* API access */}
      <Card className="grid gap-3 p-6">
        <div className="flex items-center gap-3">
          <Icon name="lock" className="text-candle" />
          <h2 className="font-serif text-xl text-parchment">API-tilgang</h2>
        </div>
        <p className="text-sm text-mist">
          Personlige tokens for CLI, scripts og integrasjoner. Administreres på admin-siden.
        </p>
        <div>
          <Link to="/admin"><Button variant="ghost" iconRight="arrow-right">Gå til tokens</Button></Link>
        </div>
      </Card>

      {/* System status */}
      <Card className="grid gap-3 p-6">
        <div className="flex items-center gap-3">
          <Icon name="info" className="text-candle" />
          <h2 className="font-serif text-xl text-parchment">System</h2>
        </div>
        {healthQ.isLoading ? (
          <Skeleton height={48} />
        ) : healthQ.data ? (
          <div className="grid gap-2 sm:grid-cols-2">
            <div className="flex items-center gap-2 text-sm">
              <Badge tone={healthQ.data.ok ? 'success' : 'danger'}>{healthQ.data.ok ? 'OK' : 'NEDE'}</Badge>
              <span className="text-mist">Backend</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Badge tone={healthQ.data.printing.ok === false ? 'danger' : 'gold'}>
                {healthQ.data.printing.mode}
              </Badge>
              <span className="text-mist">Print-modus</span>
            </div>
            <div className="col-span-full">
              <h3 className="mb-2 mt-2 text-xs uppercase tracking-widest text-mist">Strekkode-providere</h3>
              <div className="flex flex-wrap gap-2">
                {healthQ.data.providers.map((p) => (
                  <Pill key={p.name} variant={p.enabled ? 'gold' : 'default'} icon={p.enabled ? 'check' : 'x'}>
                    {p.name}{p.enabled ? '' : p.reason === 'missing_api_key' ? ' · mangler nøkkel' : ''}
                  </Pill>
                ))}
              </div>
            </div>
            <div className="col-span-full">
              <h3 className="mb-2 mt-2 text-xs uppercase tracking-widest text-mist">AI</h3>
              <div className="flex flex-wrap gap-2">
                <Pill variant={healthQ.data.ai?.openai?.enabled ? 'gold' : 'default'} icon={healthQ.data.ai?.openai?.enabled ? 'check' : 'x'}>
                  OpenAI {healthQ.data.ai?.openai?.enabled ? 'klar' : 'mangler nøkkel'}
                </Pill>
                <Pill variant={healthQ.data.ai?.recommendations?.enabled ? 'gold' : 'default'} icon="sparkles">
                  Anbefalinger · {healthQ.data.ai?.recommendations?.mode ?? 'rules-fallback'}
                </Pill>
              </div>
            </div>
          </div>
        ) : (
          <p className="text-sm text-mist">Får ikke kontakt med backend.</p>
        )}
      </Card>

      {/* About */}
      <Card className="p-6">
        <div className="flex items-center gap-4">
          <Logo size={32} showWordmark={false} />
          <div className="grid">
            <h2 className="font-serif text-xl text-parchment">{t('settings.aboutTitle')}</h2>
            <p className="text-xs text-mist">
              Frontend v{appVersion} · build {appBuildSha} · {formattedBuildTime(i18n.language.startsWith('en') ? 'en-GB' : 'nb-NO')}
            </p>
            {user && <p className="text-xs text-mist">Logget inn som {user.email}</p>}
          </div>
        </div>
      </Card>

      {/* Danger zone */}
      <Card className="grid gap-3 p-6">
        <div className="flex items-center gap-3">
          <Icon name="logout" className="text-[color:var(--ui-color-danger)]" />
          <h2 className="font-serif text-xl text-parchment">Konto</h2>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="ghost" iconLeft="logout" onClick={handleSignOut}>
            {t('nav.signOut')}
          </Button>
        </div>
      </Card>
    </div>
  );
}

// ─── Profile card (display name + email) ──────────────────────────────

function ProfileCard() {
  const { user, setSession, memberships } = useSession();
  const { push } = useToasts();
  const [draft, setDraft] = useState('');
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (user?.displayName) setDraft(user.displayName);
  }, [user?.displayName]);

  const save = useMutation({
    mutationFn: () => Auth.updateMe({ displayName: draft }),
    onSuccess: (updated) => {
      setSession({ user: updated, memberships });
      setDirty(false);
      push({ title: 'Lagret', tone: 'success' });
    },
    onError: (err: unknown) => {
      // Graceful fallback: backend hasn't shipped PATCH /me yet → save locally.
      const status = (err as { status?: number })?.status;
      if (status === 404 || status === 405) {
        if (user) setSession({ user: { ...user, displayName: draft }, memberships });
        setDirty(false);
        push({
          title: 'Lagret lokalt',
          description: 'Server-PATCH /api/v1/me er ikke enabled enda — vi husker det her i nettleseren.',
          tone: 'info',
        });
        return;
      }
      const message = err instanceof Error ? err.message : 'Ukjent feil';
      push({ title: 'Kunne ikke lagre', description: message, tone: 'danger' });
    },
  });

  if (!user) return null;

  return (
    <Card className="grid gap-4 p-6">
      <div className="flex items-center gap-3">
        <Icon name="user" className="text-candle" />
        <h2 className="font-serif text-xl text-parchment">Profil</h2>
      </div>
      <div className="grid gap-3 sm:grid-cols-[auto_1fr] sm:items-start">
        <div className="grid h-16 w-16 place-items-center rounded-full bg-candle/15 text-candle font-serif text-2xl">
          {(user.displayName || user.email)[0]?.toUpperCase()}
        </div>
        <div className="grid gap-3">
          <Input
            label="Visningsnavn"
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              setDirty(true);
            }}
          />
          <Input label="E-post" value={user.email} disabled hint="Endring av e-post krever ny innlogging." />
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="primary"
              iconLeft="check"
              loading={save.isPending}
              disabled={!dirty || draft.trim().length < 1}
              onClick={() => save.mutate()}
            >
              Lagre
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}

// ─── Theme section ────────────────────────────────────────────────────

function ThemeSection() {
  const { theme, setTheme } = useTheme();
  const opt = (id: ThemeName, label: string, icon: 'moon' | 'sun', hint: string) => (
    <button
      type="button"
      onClick={() => setTheme(id)}
      className={`ui-card grid gap-1 p-3 text-left transition-colors ${theme === id ? 'border-candle' : 'hover:border-line-soft'}`}
      aria-pressed={theme === id}
    >
      <div className="flex items-center gap-2">
        <Icon name={icon} size={14} className="text-candle" />
        <span className="text-sm font-semibold text-parchment">{label}</span>
        {theme === id && <Icon name="check" size={12} className="ml-auto text-candle" />}
      </div>
      <span className="text-[11px] text-mist">{hint}</span>
    </button>
  );
  return (
    <div className="grid gap-2">
      <div className="text-xs uppercase tracking-widest text-mist">Tema</div>
      <div className="grid gap-2 sm:grid-cols-2">
        {opt('alkisen-dark', 'Mørk (kjeller)', 'moon', 'Sommelier-stemning, varm gulltone')}
        {opt('alkisen-light', 'Lys (pergament)', 'sun', 'Varm pergament, lys for dagslys')}
      </div>
    </div>
  );
}

// ─── Notifications section ────────────────────────────────────────────

interface NotificationsPrefs {
  drinkWindowEmail?: boolean;
  memberAddedEmail?: boolean;
  weeklyDigest?: boolean;
}

function NotificationsSection() {
  const { user, memberships, setSession } = useSession();
  const { push } = useToasts();
  const initial: NotificationsPrefs =
    (user?.prefs?.notifications as NotificationsPrefs | undefined) ?? {};
  const [prefs, setPrefs] = useState<NotificationsPrefs>(initial);
  const [serverDisabled, setServerDisabled] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setPrefs((user?.prefs?.notifications as NotificationsPrefs | undefined) ?? {});
  }, [user?.prefs]);

  const save = useMutation({
    mutationFn: () =>
      Auth.updatePrefs({ ...(user?.prefs ?? {}), notifications: prefs }),
    onSuccess: (updated) => {
      setSession({ user: updated, memberships });
      setDirty(false);
      push({ title: 'Varsler oppdatert', tone: 'success' });
    },
    onError: (err: unknown) => {
      const status = (err as { status?: number })?.status;
      if (status === 404 || status === 405) {
        if (user) setSession({ user: { ...user, prefs: { ...(user.prefs ?? {}), notifications: prefs } }, memberships });
        setDirty(false);
        setServerDisabled(true);
        push({
          title: 'Lagret lokalt',
          description: 'Server-PATCH /api/v1/me/prefs er ikke enabled enda — vi husker det her i nettleseren.',
          tone: 'info',
        });
        return;
      }
      const message = err instanceof Error ? err.message : 'Ukjent feil';
      push({ title: 'Kunne ikke lagre', description: message, tone: 'danger' });
    },
  });

  const flip = (key: keyof NotificationsPrefs) => {
    setPrefs((p) => ({ ...p, [key]: !p[key] }));
    setDirty(true);
  };

  return (
    <div className="grid gap-2">
      <div className="text-xs uppercase tracking-widest text-mist">Varsler</div>
      <div className="grid gap-2 rounded-md border border-line bg-ink-soft p-3 text-sm">
        <ToggleRow
          label="E-post når en flaske nærmer seg slutten av drikkevinduet"
          checked={Boolean(prefs.drinkWindowEmail)}
          onChange={() => flip('drinkWindowEmail')}
        />
        <ToggleRow
          label="E-post når et husholdningsmedlem legger til flasker"
          checked={Boolean(prefs.memberAddedEmail)}
          onChange={() => flip('memberAddedEmail')}
        />
        <ToggleRow
          label="Ukentlig kjeller-sammendrag"
          checked={Boolean(prefs.weeklyDigest)}
          onChange={() => flip('weeklyDigest')}
        />
        <div className="flex items-center justify-between gap-2 pt-1">
          {serverDisabled ? (
            <span className="text-[10px] uppercase tracking-widest text-mist/70">
              Lagret lokalt — backend støtter ikke <code>PATCH /me/prefs</code> enda.
            </span>
          ) : (
            <span className="text-[10px] uppercase tracking-widest text-mist/70">
              Krever at backend faktisk sender e-post.
            </span>
          )}
          <Button
            size="sm"
            variant="primary"
            iconLeft="check"
            loading={save.isPending}
            disabled={!dirty}
            onClick={() => save.mutate()}
          >
            Lagre varsler
          </Button>
        </div>
      </div>
    </div>
  );
}

function ToggleRow({ label, checked, onChange }: { label: string; checked: boolean; onChange: () => void }) {
  return (
    <label className="flex cursor-pointer items-center justify-between gap-3 rounded-md px-2 py-1.5 hover:bg-overlay-soft">
      <span className="text-parchment">{label}</span>
      <input type="checkbox" checked={checked} onChange={onChange} className="h-4 w-4 accent-[color:var(--ui-color-candle)]" />
    </label>
  );
}

// ─── Print target selector ────────────────────────────────────────────

function PrintTargetSection({
  printersQ,
}: {
  printersQ: ReturnType<typeof useQuery<{ printers: { name: string; isDefault?: boolean }[] }>>;
}) {
  const { printTarget, setPrintTarget } = usePrefs();
  const { push } = useToasts();
  const [manualPrinterName, setManualPrinterName] = useState(printTarget?.printerName ?? '');
  const saveManual = () => {
    const printerName = manualPrinterName.trim();
    if (!printerName) {
      setPrintTarget({ type: 'queue' });
      push({ title: 'Print satt til kø', tone: 'info' });
      return;
    }
    setPrintTarget({ type: 'local', printerName });
    push({ title: 'Skriver lagret', description: printerName, tone: 'success' });
  };
  return (
    <div className="grid gap-2">
      <div className="text-xs uppercase tracking-widest text-mist">Standard skriver</div>
      {printTarget?.printerName && (
        <div className="flex flex-wrap items-center gap-2 text-xs text-mist">
          <Badge tone="gold">Valgt</Badge>
          <span>{printTarget.printerName}</span>
        </div>
      )}
      {printersQ.isLoading ? (
        <Spinner />
      ) : (printersQ.data?.printers.length ?? 0) === 0 ? (
        <div className="text-xs text-mist">
          Ingen skrivere oppdaget. Endre <code>LABEL_PRINT_MODE</code> til <code>local</code> i backend, eller
          koble til en skriver-agent på enheten.
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          <Pill
            active={!printTarget?.printerName}
            onClick={() => setPrintTarget({ type: 'queue' })}
          >
            Kun kø (ingen direkte print)
          </Pill>
          {printersQ.data!.printers.map((p) => (
            <Pill
              key={p.name}
              active={printTarget?.printerName === p.name}
              icon="print"
              onClick={() =>
                setPrintTarget({ type: 'local', printerName: p.name })
              }
            >
              {p.name}{p.isDefault ? ' (default)' : ''}
            </Pill>
          ))}
        </div>
      )}
      <div className="grid gap-2 rounded-md border border-line bg-ink-soft p-3 sm:grid-cols-[1fr_auto_auto] sm:items-end">
        <Input
          label="Legg til skriver manuelt"
          placeholder="Zebra_Technologies_ZTC_ZD421_203dpi_ZPL"
          value={manualPrinterName}
          onChange={(e) => setManualPrinterName(e.target.value)}
          hint="Bruk CUPS-/lpstat-navn, eller lagre et navn som lokal agent kan bruke senere."
        />
        <Button variant="primary" iconLeft="print" onClick={saveManual}>
          Lagre skriver
        </Button>
        <Button
          variant="ghost"
          onClick={() => {
            setManualPrinterName('');
            setPrintTarget({ type: 'queue' });
          }}
        >
          Kun kø
        </Button>
      </div>
    </div>
  );
}
